import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, AlertTriangle, Flag, Clock, Mail } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useIsMobile } from '../hooks/useIsMobile';
import type { Contact } from '../types';

function timeGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function dotColor(actionName: string = ''): string {
  const a = actionName.toUpperCase();
  if (a === 'CREATE' || a === 'VERIFY' || a === 'FLAG_RESOLVED') return 'var(--green)';
  if (a === 'ARCHIVE' || a === 'DEACTIVATE') return 'var(--red)';
  if (a === 'FLAG') return 'var(--amber)';
  if (a === 'UPDATE') return 'var(--electric)';
  if (a === 'CONTACT_SENT' || a === 'EMAIL_SENT') return '#7c3aed';
  if (a === 'LOGIN') return 'var(--gray-400)';
  return 'var(--gray-400)';
}

function getEntryName(entry: any): string {
  const nv = entry.new_value;
  if (!nv) return '';
  if (typeof nv.full_name === 'string' && nv.full_name) return nv.full_name;
  const first = typeof nv.first_name === 'string' ? nv.first_name : '';
  const last  = typeof nv.last_name  === 'string' ? nv.last_name  : '';
  return `${first} ${last}`.trim();
}

function describeEntry(entry: any): string {
  const a = (entry.action_name ?? '').toUpperCase();
  const name = getEntryName(entry);
  const suffix = name ? ` — ${name}` : '';
  const who = entry.changed_by_name ?? 'User';
  switch (a) {
    case 'CREATE':        return `New contact added${suffix}`;
    case 'UPDATE':        return `Contact updated${suffix}`;
    case 'VERIFY':        return `Contact marked verified${suffix}`;
    case 'ARCHIVE':       return `Contact archived${suffix}`;
    case 'DEACTIVATE':    return `Contact deactivated${suffix}`;
    case 'FLAG':          return `Flag raised${suffix}`;
    case 'FLAG_RESOLVED': return `Flag resolved${suffix}`;
    case 'CONTACT_SENT':  return `Contact info sent`;
    case 'EMAIL_SENT':    return `Email sent`;
    case 'LOGIN':         return `${who} signed in`;
    case 'EXPORT':        return `Directory exported`;
    case 'BULK_IMPORT':   return `Bulk import`;
    default:              return `${entry.entity_type ?? 'Record'} ${a.toLowerCase()}`;
  }
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { agingDays } = useSettings();
  const isMobile = useIsMobile();
  const [q, setQ] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try { return new Set<string>(JSON.parse(sessionStorage.getItem('iad2_dismissed_alerts') || '[]')); }
    catch { return new Set<string>(); }
  });

  const isSuperuser = user?.role === 'Administrator' || user?.role === 'Superuser';

  // KPI data (Superuser+ only)
  const { data: kpi } = useQuery({
    queryKey: ['kpi-dash'],
    queryFn: () => api.getKPI(),
    enabled: isSuperuser,
    staleTime: 60_000,
    retry: false,
  });

  // Audit log for Recent Activity (Superuser+)
  const { data: auditData } = useQuery({
    queryKey: ['dash-audit'],
    queryFn: () => api.getAuditLog({ limit: 8 }),
    enabled: isSuperuser,
    staleTime: 60_000,
    retry: false,
  });
  const auditEntries: any[] = auditData?.data ?? [];

  // Recent/search contacts (all roles)
  const { data: contactsData, isLoading: contactsLoading } = useQuery({
    queryKey: ['dash-contacts', searchTerm],
    queryFn: () => api.searchContacts({
      q: searchTerm || undefined,
      active_only: 'true',
      limit: 10,
    }),
    staleTime: 30_000,
  });
  const contacts: Contact[] = contactsData?.data ?? [];

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(q);
  }, [q]);

  const dismiss = (key: string) => {
    const next = new Set(dismissed).add(key);
    setDismissed(next);
    sessionStorage.setItem('iad2_dismissed_alerts', JSON.stringify(Array.from(next)));
  };

  // KPI metric extractions
  const totalContacts      = kpi?.records?.total ?? 0;
  const verifiedCount      = kpi?.freshness?.verified ?? 0;
  const agingCount         = kpi?.freshness?.aging ?? 0;
  const overdueCount       = kpi?.freshness?.overdue ?? 0;
  const neverVerifiedCount = kpi?.freshness?.never_verified ?? 0;
  const openFlags          = kpi?.flags?.open ?? 0;
  const pendingChanges     = (kpi as any)?.pending_changes?.pending ?? 0;
  const activeListsCount   = kpi?.lists?.active ?? 0;
  const updateReqPending   = (kpi as any)?.update_requests?.pending ?? 0;
  const expiredCount       = kpi?.update_requests?.expired ?? 0;

  const alerts = [
    agingCount > 0    && { key: 'aging',    color: 'var(--amber-text)', border: 'var(--amber)', bg: 'var(--amber-50)', icon: <AlertTriangle size={13}/>, label: `${agingCount} contacts aging (>${agingDays} days since verification)`, to: '/directory?verification=aging' },
    openFlags > 0     && { key: 'flags',    color: 'var(--red)', border: 'var(--red)', bg: 'var(--red-50)', icon: <Flag size={13}/>,          label: `${openFlags} open flags`,                                    to: '/flags' },
    pendingChanges > 0 && { key: 'pending', color: 'var(--red)', border: 'var(--red)', bg: 'var(--red-50)', icon: <Clock size={13}/>,        label: `${pendingChanges} pending changes awaiting review`,           to: '/pending' },
    expiredCount > 0  && { key: 'expiring', color: 'var(--amber-text)', border: 'var(--amber)', bg: 'var(--amber-50)', icon: <Mail size={13}/>,         label: `${expiredCount} update requests expired`,                    to: '/update-requests' },
  ].filter((a): a is Exclude<typeof a, false> => Boolean(a) && !dismissed.has((a as any).key));

  const stats = isSuperuser ? [
    { label: 'Total Contacts',  value: totalContacts.toLocaleString(),       border: 'var(--electric)',  to: '/directory' },
    { label: 'Verified',        value: verifiedCount.toLocaleString(),        border: 'var(--green)',  to: '/directory?verification=verified' },
    { label: 'Aging',           value: agingCount.toLocaleString(),           border: 'var(--amber)',  to: '/directory?verification=aging' },
    { label: 'Overdue',         value: overdueCount.toLocaleString(),         border: 'var(--red)',  to: '/directory?verification=overdue' },
    { label: 'Never Verified',  value: neverVerifiedCount.toLocaleString(),   border: 'var(--gray-400)',  to: '/directory?verification=never' },
    { label: 'Pending Changes', value: pendingChanges.toLocaleString(),       border: 'var(--hazard-deep)',  to: '/pending' },
    { label: 'Raised Flags',    value: openFlags.toLocaleString(),            border: 'var(--amber)',  to: '/flags' },
    { label: 'Active Lists',    value: activeListsCount.toLocaleString(),     border: 'var(--electric)',  to: '/lists' },
    { label: 'Update Requests', value: updateReqPending.toLocaleString(),     border: '#6366f1',  to: '/update-requests' },
  ] : [];

  const firstName = user?.first_name || user?.email?.split('@')[0] || '';

  return (
    <div style={{ maxWidth: 900, padding: '0 0 40px' }}>

      {/* Welcome strip */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 900 }}>
          {timeGreeting()}{firstName ? `, ${firstName}` : ''}.
        </h1>
        {alerts.length > 0 ? (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--gray-500)' }}>
            You have {alerts.length} item{alerts.length !== 1 ? 's' : ''} requiring attention.
          </p>
        ) : (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--gray-500)' }}>Everything looks good.</p>
        )}
      </div>

      {/* Alert pills */}
      {alerts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : undefined, flexWrap: isMobile ? undefined : 'wrap', gap: 8, marginBottom: 24 }}>
          {alerts.map((alert) => (
            <div
              key={alert.key}
              onClick={() => navigate(alert.to)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: alert.bg, color: alert.color,
                border: `1px solid ${alert.border}`,
                borderRadius: 20, padding: '5px 12px',
                fontSize: 14, fontWeight: 500, cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              {alert.icon}
              <span>{alert.label}</span>
              <span
                onClick={(e) => { e.stopPropagation(); dismiss(alert.key); }}
                style={{ marginLeft: 4, opacity: 0.6, fontSize: 15, lineHeight: 1, cursor: 'pointer' }}
              >×</span>
            </div>
          ))}
        </div>
      )}

      {/* Stat tiles */}
      {stats.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 24 }}>
          {stats.map((s) => (
            <div
              key={s.label}
              onClick={() => navigate(s.to)}
              style={{
                position: 'relative',
                background: 'white',
                borderRadius: 8,
                border: '1px solid var(--gray-200)',
                borderTop: `3px solid ${s.border}`,
                padding: isMobile ? '8px 10px' : '10px 12px',
                cursor: 'pointer',
                transition: 'box-shadow 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
            >
              <div style={{ position: 'absolute', top: 8, right: 10, fontSize: 12, color: 'var(--gray-500)' }}>→</div>
              <div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 700, color: 'var(--nycem-blue)', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 3 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="card" style={{ marginBottom: 16 }}>
        <form onSubmit={handleSearch}>
          <div className="searchbar">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search contacts by name, agency, or role…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
            />
            <button type="submit" className="btn btn-primary" style={{ marginLeft: 8 }}>
              Search
            </button>
            {searchTerm && (
              <button type="button" className="btn btn-secondary" style={{ marginLeft: 4 }}
                onClick={() => { setQ(''); setSearchTerm(''); }}>
                Clear
              </button>
            )}
          </div>
        </form>

        {/* Results */}
        <div style={{ marginTop: 12 }}>
          {!searchTerm && (
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              Recently Updated
            </div>
          )}
          {contactsLoading ? (
            <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--gray-500)', fontSize: 14 }}>Loading…</div>
          ) : contacts.length === 0 ? (
            <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--gray-500)', fontSize: 14 }}>No contacts found</div>
          ) : (
            <div>
              {contacts.map((c) => {
                const name = c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
                const phone = c.phones?.find((p) => p.is_preferred)?.phone_number ?? c.preferred_phones?.[0] ?? null;
                return (
                  <div
                    key={c.id}
                    onClick={() => navigate(`/contacts/${c.id}`)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '9px 0', borderBottom: '1px solid var(--gray-100)',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{name}</div>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                        {c.agency_acronym ?? c.agency_name ?? ''}{c.title ? ` · ${c.title}` : ''}
                      </div>
                    </div>
                    {phone && !isMobile && <div style={{ fontSize: 12, color: 'var(--gray-500)', flexShrink: 0 }}>{phone}</div>}
                    {!isMobile && (
                      <span className={`pill ${c.classification_id === 2 ? 'pill-restricted' : 'pill-standard'}`} style={{ fontSize: 12, flexShrink: 0 }}>
                        {c.classification_id === 2 ? 'Restricted' : 'Standard'}
                      </span>
                    )}
                  </div>
                );
              })}
              <div style={{ marginTop: 10, textAlign: 'center' }}>
                <button
                  className="btn btn-secondary"
                  style={{ fontSize: 12 }}
                  onClick={() => navigate(searchTerm ? `/directory?q=${encodeURIComponent(searchTerm)}` : '/directory')}
                >
                  {searchTerm ? 'See all results in Directory' : 'Open full Directory'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity — Superuser+ only, from audit log */}
      {isSuperuser && auditEntries.length > 0 && (
        <div className="card">
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Recent Activity
          </div>
          {auditEntries.map((entry: any, i: number) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '6px 0',
                borderBottom: i < auditEntries.length - 1 ? '1px solid var(--gray-100)' : 'none',
              }}
            >
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor(entry.action_name), flexShrink: 0, marginTop: 5 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: 'var(--gray-700)', lineHeight: 1.4 }}>{describeEntry(entry)}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                  {entry.changed_at ? timeAgo(entry.changed_at) : ''}
                  {entry.changed_by_name ? ` · ${entry.changed_by_name}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
